import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

const TYPING_DELAY = 500
const NO_RESULTS = "<li class='no-results'>No teams found.</li>"
// `$http`'s default Accept, kept whole: the action renders an HTML partial into its JSON body,
// so dropping `*/*` narrows the lookup context to :json and the partial goes missing.
const ACCEPT = "application/json, text/plain, */*"

export default class extends Controller {
  static get targets() { return ["search", "loading", "results"]; }

  // The debounce is the only thing this controller holds. Left armed, it fires against targets the
  // teardown has already taken away.
  disconnect() {
    clearTimeout(this.timer);
    this.timer = null;
  }

  search(event) {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.performSearch(event), TYPING_DELAY);
  }

  performSearch(event) {
    event.stopPropagation();

    const { url, urlkey, lassoV2 } = this.searchTarget.dataset;
    const searchUrl = `${url}?q=${this.searchTarget.value}&url_key=${urlkey}&lasso_v2=${lassoV2}`;

    this.showLoading();

    const options = RequestHelper.defaultRequestOptions();
    options.headers = Object.assign({}, options.headers, { "Accept": ACCEPT });

    fetch(searchUrl, options).
      then(response => {
        if (!response.ok) { throw new Error(`Team search failed (${response.status})`); }
        return response.json();
      }).
      then(({ teams }) => this.renderResults(teams, lassoV2)).
      catch(error => {
        console.warn(error);
        this.hideLoading();
      });
  }

  renderResults(teams, lassoV2) {
    if (lassoV2) {
      this.discard(".result, .no-results");
      this.append(teams, team => team);
    } else {
      this.discard("li.result, li.no-results");
      this.append(teams, team => `<li class="result">${team}</li>`);
      this.hideLoading();
    }
  }

  // S2, accepted: every `team` is server-rendered HTML from our own origin. Subdomain::Accounts::
  // TeamsController#search maps its search results through
  // `render_to_string(partial: "shared/lasso/dashboard/team", layout: false, ...)` and returns them
  // as the `teams` array -- so `wrap(team)` either passes that markup straight through (lasso v2)
  // or nests it in a fixed `<li class="result">`. Read _team.html.haml: no `raw`/`html_safe`/`!=`,
  // `= team.name` is HAML-escaped, and team_logo's only output is `image_tag` or `= initials`,
  // both escaped by Rails' own tag builder. NO_RESULTS is the module constant above, a fixed
  // literal with nothing interpolated into it.
  append(teams, wrap) {
    if (teams.length > 0) {
      teams.forEach(team => this.resultsTarget.insertAdjacentHTML("beforeend", wrap(team)));
    } else {
      this.resultsTarget.insertAdjacentHTML("beforeend", NO_RESULTS);
    }
  }

  discard(selector) {
    this.resultsTarget.querySelectorAll(selector).forEach(node => node.remove());
  }

  // jQuery, so the spinner gets back the computed display `.hide()` recorded. The lasso v2 menu
  // renders no `.loading` at all, where jQuery's empty set made both calls no-ops.
  showLoading() { if (this.hasLoadingTarget) { $(this.loadingTarget).show(); } }

  hideLoading() { if (this.hasLoadingTarget) { $(this.loadingTarget).hide(); } }
}
